(ns lupapalvelu.ui.navbar-connected
  (:require [lupapalvelu.next.event :refer [<sub]]
            [lupapalvelu.next.session :as sess]
            [lupapalvelu.ui.authorization :as auth]
            [lupapalvelu.ui.common :refer [loc open-page]]
            [lupapalvelu.ui.hooks :refer [use-computed use-observable]]
            [lupapalvelu.ui2.icons :as icons]
            [lupapalvelu.ui2.navbar :refer [navbar]]
            [re-frame.core :as rf]
            [reagent.core :refer [defc]]
            [reagent.hooks :as hooks]
            [reitit.frontend.easy :as rfe]))

(rf/reg-sub ::navbar-context
  :<- [:route/match]
  (fn [route-match _]
    (let [navbar (get-in route-match [:data :navbar])
          app-id (get-in route-match [:parameters :path :app-id])]
      (case navbar
        :dashboard   {:type :dashboard}
        :application {:type :application :app-id app-id}
        nil))))

(rf/reg-event-fx ::fetch-assignment-count
  (fn [_ _]
    (when (auth/global-auth? :assignment-count)
      {:fx [[:action/query {:name    :assignment-count
                            :success [::store-assignment-count]}]]})))

(rf/reg-event-db ::store-assignment-count
  (rf/path [::data :assignment-count])
  (fn [_ [_ {:keys [assignmentCount]}]]
    (or assignmentCount 0)))

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::assignment-count
  :<- [::data]
  :-> :assignment-count)

(defn- dashboard-href []
  (rfe/href :dashboard))

(defn- applications-href []
  (rfe/href :applications))

(defn- placeholder-click [label]
  (fn [_]
    (js/console.info (str "Navbar placeholder clicked: " label))))

;; KnockoutJS bridge: subscribe to hub `application-loaded` events to keep the
;; address reactive. Dispatch on mount too, to guard against the KO model firing
;; before this component subscribes.
(rf/reg-event-fx ::application-loaded
  (fn [{db :db} [_ route-app-id]]
    (let [address (some-> js/lupapisteApp .-models .-application .-address (#(%)))]
      (if (= route-app-id (some-> js/lupapisteApp .-models .-application .-id (#(%))))
        {:db (assoc-in db [::data :application-address] address)}
        {}))))

(rf/reg-event-db ::clear-application-address
  (rf/path [::data])
  (fn [data _]
    (dissoc data :application-address)))

(rf/reg-sub ::application-address
  :<- [::data]
  :-> :application-address)

(rf/reg-sub ::dashboard-actions
  :<- [:auth/global? :view-tasks]
  :<- [:auth/global? :view-messages]
  :<- [::assignment-count]
  (fn [[view-tasks? view-messages? assignment-count] _]
    (cond-> []
      ;; Placeholder for assignment-related navbar actions; keep this until the UI is wired.
      view-tasks?    (conj {:key   :tasks
                            :label (loc "navbar.dashboard.tasks")
                            :icon  icons/task-complete
                            :badge (when (pos? assignment-count) assignment-count)
                            :items [{:key      :open-tasks
                                     :label    (loc "navbar.dashboard.tasks.open")
                                     :on-click (placeholder-click (loc "navbar.dashboard.tasks.open"))}]})
      ;; Placeholder for message-related navbar actions.
      view-messages? (conj {:key   :messages
                            :label (loc "navbar.dashboard.messages")
                            :icon  icons/chat
                            :items [{:key      :open-messages
                                     :label    (loc "navbar.dashboard.messages.open")
                                     :on-click (placeholder-click (loc "navbar.dashboard.messages.open"))}]}))))

(rf/reg-sub ::application-action-items
  :<- [::sess/language]
  :<- [:auth/application? :add-operation]
  :<- [:auth/application? :pdf-export]
  :<- [:auth/application? :copy-application]
  :<- [:auth/application? :cancel-application]
  (fn [[language add-op? pdf-export? copy-app? cancel-app?] [_ app-id]]
    (cond-> []
      add-op?     (conj {:key      :add-operation
                         :label    (loc "application.addOperation")
                         :icon     icons/add
                         :on-click #(-> js/lupapisteApp .-models .-application .addOperation)})
      pdf-export? (conj {:key   :pdf-export
                         :label (loc "application.pdf")
                         :icon  icons/printer
                         :href  (str "/api/raw/pdf-export?id=" app-id "&lang=" language)})
      copy-app?   (conj {:key      :copy-application
                         :label    (loc "application.copy")
                         :icon     icons/copy-file
                         :on-click #(-> js/lupapisteApp .-models .-application .copy)})
      cancel-app? (conj {:key      :cancel-application
                         :label    (loc "application.cancelApplication")
                         :icon     icons/misuse
                         :on-click #(-> js/lupapisteApp .-models .-application .cancelApplication)}))))

(defc ^:private dashboard-navbar []
  (hooks/use-effect #(rf/dispatch [::fetch-assignment-count]))
  (let [create-application? (<sub [:auth/global? :create-application])]
    [navbar {:breadcrumbs      [{:label (loc "navbar.dashboard.title")}]
             :actions          (<sub [::dashboard-actions])
             ;; Both CTAs open the same "create new" flow (`create-part-1`); the
             ;; user chooses application vs. inforequest later in that flow. See
             ;; legacy `LUPAPISTE.CreateApplicationModel` in create.js.
             :secondary-action (when create-application?
                                 {:label    (loc "navbar.dashboard.new-inforequest")
                                  :on-click #(open-page "create-part-1")})
             :primary-action   (when create-application?
                                 {:label    (loc "navbar.dashboard.new-application")
                                  :on-click #(open-page "create-part-1")})}]))

;; side-panel-control-model.js
(def panels
  {:conversation {:get-observable (fn [^js service] (.-showConversationPanel service))
                  :track-event    "openConversation"
                  :hub-event      "SidePanelService::UnseenCommentsSeen"}
   :notice       {:get-observable (fn [^js service] (.-showNoticePanel service))
                  :track-event    "openNotice"
                  :hub-event      "SidePanelService::NoticeSeen"}
   :notes        {:get-observable (fn [^js service] (.-showCompanyNotesPanel service))
                  :track-event    "openCompanyNotes"
                  :hub-event      "SidePanelService::CompanyNotesSeen"}
   :info         {:get-observable (fn [^js service] (.-showInfoPanel service))
                  :track-event    "openInfo"
                  :hub-event-fn   (fn [open]
                                    (if open
                                      (.send js/hub "infoService::fetch-links" #js {:markSeen true})
                                      (.send js/hub "infoService::fetch-links" #js {:reset true})))}})

(defn toggle-legacy-panel [n]
  (let [service   (.. js/lupapisteApp -services -sidePanelService)

        {:keys [get-observable track-event hub-event hub-event-fn]}
        (get panels n)
        obs       (get-observable service)
        old-state (obs)
        new-state (not old-state)]
    (obs new-state)
    ;; close others
    (doseq [[_ {:keys [get-observable]}] (dissoc panels n)]
      (let [obs (get-observable service)]
        (obs false)))

    ;; waveFlag
    (when (and new-state track-event)
      (.send js/hub "track-click" #js {:category "side-panel"
                                       :label    "side-panel"
                                       :event    track-event}))
    (cond
      hub-event-fn (hub-event-fn new-state)
      hub-event (when new-state
                  (.send js/hub hub-event)))))

(defc ^:private application-navbar [{:keys [app-id]}]
  (hooks/use-effect
    (fn []
      (rf/dispatch [::application-loaded app-id])
      (let [token (js/hub.subscribe "application-loaded"
                                    #(rf/dispatch [::application-loaded app-id]))]
        (fn []
          (js/hub.unsubscribe token)
          (rf/dispatch [::clear-application-address]))))
    [app-id])
  (let [action-items               (<sub [::application-action-items app-id])
        service                    (.. js/lupapisteApp -services -sidePanelService)
        ;; Could be a bit nicer to make each of the actions their own component,
        ;; could call hooks only if the action is shown etc.
        ;; navbar component currently takes actions as data, which isn't very composable.
        auth?                      (fn [action] (.. js/lupapisteApp -models -applicationAuthModel (ok (name action))))
        show-conversation          (use-computed (fn []
                                                   (or (auth? :comments)
                                                       (auth? :can-target-comment-to-authority)
                                                       (auth? :can-mark-answered))))
        unseen-comments            (use-computed (fn [] (.. js/lupapisteApp -models -application unseenComments)))
        show-notice-control        (use-computed (fn [] (auth? :authority-notice)))
        unseen-notice              (use-observable (.-unseenNotice service))
        show-company-notes-control (use-computed (fn [] (auth? :company-notes)))]
    [navbar {:breadcrumbs [{:label (loc "navbar.dashboard.title")
                            :href  (dashboard-href)}
                           {:label (loc "requests")
                            :href  (applications-href)}
                           {:label (or (not-empty (<sub [::application-address]))
                                       app-id)}]
             :actions     (->> [(when show-conversation
                                  {:key      :messages
                                   :label    (loc :conversation.title)
                                   :badge    unseen-comments
                                   :on-click (fn [_e] (toggle-legacy-panel :conversation))})
                                ;; Notice icon logic, warning, pending etc.?
                                (when show-notice-control
                                  {:key      :notice
                                   :label    (loc :notice.title)
                                   :on-click (fn [_e] (toggle-legacy-panel :notice))
                                   :badge    (when unseen-notice
                                               "#")})
                                (when show-company-notes-control
                                  {:key      :company-notes
                                   :label    (loc :company-notes.title)
                                   :on-click (fn [_e] (toggle-legacy-panel :notes))})
                                {:key      :info
                                 :label    (loc :info.button)
                                 :on-click (fn [_e] (toggle-legacy-panel :info))}
                                (when (seq action-items)
                                  {:key   :actions-menu
                                   :label (loc "navbar.actions.label")
                                   :icon  icons/overflow-menu-vertical
                                   :items action-items})]
                               (keep identity)
                               vec)}]))

(defc connected-navbar []
  (let [context (<sub [::navbar-context])]
    (case (:type context)
      :dashboard   [dashboard-navbar]
      :application [application-navbar {:app-id (:app-id context)}]
      nil)))
